import { useState } from 'react';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import { useBaby } from '@/contexts/BabyContext';
import { addMealLog } from '@/lib/meal-firestore';
import type { MealQuantity, MealTexture, MealReaction } from '@/types';

interface MealLogSheetProps {
  isOpen: boolean;
  onClose: () => void;
  plannedMeal?: string;
  onSaved?: () => void;
}

type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';

const MEAL_TYPES: { value: MealType; label: string; emoji: string }[] = [
  { value: 'breakfast', label: 'Breakfast', emoji: '🌅' },
  { value: 'lunch',     label: 'Lunch',     emoji: '☀️'  },
  { value: 'dinner',    label: 'Dinner',    emoji: '🌙' },
  { value: 'snack',     label: 'Snack',     emoji: '🍎' },
];

const QUANTITIES: { value: MealQuantity; label: string }[] = [
  { value: 'tasted',   label: 'Just tasted' },
  { value: 'half',     label: 'About half'  },
  { value: 'finished', label: 'Finished it' },
];

const TEXTURES: { value: MealTexture; label: string }[] = [
  { value: 'puree',       label: 'Puree'       },
  { value: 'mashed',      label: 'Mashed'      },
  { value: 'soft-lumps',  label: 'Soft lumps'  },
  { value: 'finger-food', label: 'Finger food' },
];

const REACTIONS: { value: MealReaction; label: string; positive: boolean }[] = [
  { value: 'liked',           label: 'Liked it',        positive: true  },
  { value: 'refused-spoon',   label: 'Refused spoon',   positive: true  },
  { value: 'preferred-other', label: 'Preferred other', positive: true  },
  { value: 'no-issue',        label: 'No issue',        positive: true  },
  { value: 'gas',             label: 'Gas',             positive: false },
  { value: 'loose-stool',     label: 'Loose stool',     positive: false },
  { value: 'constipated',     label: 'Constipated',     positive: false },
  { value: 'rash',            label: 'Rash',            positive: false },
];

const MealLogSheet = ({ isOpen, onClose, plannedMeal, onSaved }: MealLogSheetProps) => {
  const { activeBaby } = useBaby();

  // Guess meal type from time of day
  const guessedType = (): MealType => {
    const h = new Date().getHours();
    if (h < 11) return 'breakfast';
    if (h < 15) return 'lunch';
    if (h < 19) return 'dinner';
    return 'snack';
  };

  const [mealType, setMealType]         = useState<MealType>(guessedType());
  const [actualFoods, setActualFoods]   = useState(plannedMeal ?? '');
  const [quantity, setQuantity]         = useState<MealQuantity | null>(null);
  const [texture, setTexture]           = useState<MealTexture | null>(null);
  const [reactions, setReactions]       = useState<MealReaction[]>([]);
  const [notes, setNotes]               = useState('');
  const [saving, setSaving]             = useState(false);

  if (!isOpen) return null;

  const toggleReaction = (r: MealReaction) => {
    setReactions(prev =>
      prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]
    );
  };

  const canSave = actualFoods.trim().length > 0 && quantity !== null && texture !== null;

  const handleSave = async () => {
    if (!canSave || !activeBaby?.id || saving) return;
    setSaving(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      await addMealLog(activeBaby.id, {
        date: today,
        mealType,
        plannedMeal: plannedMeal,
        actualFoodsText: actualFoods.trim(),
        quantity: quantity!,
        texture: texture!,
        reactions,
        notes: notes.trim() || undefined,
        timestamp: new Date(),
      });
      onSaved?.();
      onClose();
      // Reset for next open
      setMealType(guessedType());
      setActualFoods(plannedMeal ?? '');
      setQuantity(null);
      setTexture(null);
      setReactions([]);
      setNotes('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-3xl pb-10 overflow-y-auto max-h-[92dvh]">

        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-3xl flex items-center justify-between px-5 pt-5 pb-3 border-b border-border/40">
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'hsl(var(--foreground))' }}>Log a meal</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full"
            style={{ background: 'hsl(var(--muted) / 0.6)' }}
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="px-5 pt-4 space-y-5">

          {/* Meal type */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Meal</p>
            <div className="flex gap-2">
              {MEAL_TYPES.map(m => (
                <button
                  key={m.value}
                  onClick={() => setMealType(m.value)}
                  className="flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-semibold transition-all active:scale-95"
                  style={
                    mealType === m.value
                      ? { background: 'hsl(var(--feed-accent))', color: '#fff' }
                      : { background: 'hsl(var(--muted) / 0.4)', color: 'hsl(var(--muted-foreground))' }
                  }
                >
                  <span className="text-base">{m.emoji}</span>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* What was given */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              What did you give?
            </p>
            {plannedMeal && (
              <p className="text-xs text-muted-foreground mb-1.5">
                Planned: <span className="font-medium text-foreground">{plannedMeal}</span>
              </p>
            )}
            <textarea
              value={actualFoods}
              onChange={e => setActualFoods(e.target.value)}
              placeholder="e.g. banana puree + rice cereal"
              rows={2}
              className="w-full rounded-xl px-3 py-2.5 text-sm resize-none outline-none"
              style={{
                border: '1.5px solid hsl(var(--border))',
                background: 'hsl(var(--muted) / 0.2)',
                color: 'hsl(var(--foreground))',
              }}
            />
          </div>

          {/* Quantity */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">How much?</p>
            <div className="flex gap-2">
              {QUANTITIES.map(q => (
                <button
                  key={q.value}
                  onClick={() => setQuantity(q.value)}
                  className="flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all active:scale-95"
                  style={
                    quantity === q.value
                      ? { background: 'hsl(var(--sleep-accent))', color: '#fff' }
                      : { background: 'hsl(var(--muted) / 0.4)', color: 'hsl(var(--muted-foreground))' }
                  }
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          {/* Texture */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Texture</p>
            <div className="flex gap-2 flex-wrap">
              {TEXTURES.map(t => (
                <button
                  key={t.value}
                  onClick={() => setTexture(t.value)}
                  className="py-2 px-3.5 rounded-full text-xs font-semibold transition-all active:scale-95"
                  style={
                    texture === t.value
                      ? { background: 'hsl(var(--diaper-accent))', color: '#fff' }
                      : { background: 'hsl(var(--muted) / 0.4)', color: 'hsl(var(--muted-foreground))' }
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reactions */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              What happened? <span className="normal-case font-normal">(optional, tap all that apply)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {REACTIONS.map(r => {
                const selected = reactions.includes(r.value);
                return (
                  <button
                    key={r.value}
                    onClick={() => toggleReaction(r.value)}
                    className="py-1.5 px-3 rounded-full text-xs font-semibold transition-all active:scale-95"
                    style={
                      selected
                        ? r.positive
                          ? { background: 'hsl(var(--sleep-accent))', color: '#fff' }
                          : { background: 'hsl(var(--primary))', color: '#fff' }
                        : r.positive
                          ? { background: 'hsl(260,60%,92%)', color: 'hsl(var(--sleep-accent))' }
                          : { background: 'hsl(340,60%,92%)', color: 'hsl(var(--primary))' }
                    }
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Notes <span className="normal-case font-normal">(optional)</span>
            </p>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Anything else to remember..."
              className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
              style={{
                border: '1.5px solid hsl(var(--border))',
                background: 'hsl(var(--muted) / 0.2)',
                color: 'hsl(var(--foreground))',
              }}
            />
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="w-full py-3.5 rounded-2xl text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-40"
            style={{ background: 'hsl(var(--feed-accent))' }}
          >
            {saving ? 'Saving...' : 'Save meal'}
          </button>

        </div>
      </div>
    </div>
  );
};

export default MealLogSheet;
